import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import NProgress from '~/components/NProgress'
import { useRouter } from 'next/router'
import { ToastProvider } from '@ticketswap/solar'
import BaseStyles from '~/components/BaseStyles'
import { page, pageSource } from '~/utils/analytics'
import LoggedInUserProvider from '~/context/LoggedInUser'
import ConsentDialog from '~/components/ConsentDialog/dynamic'
import NetworkStatus from '~/components/NetworkStatus'
import { withServerResponse } from '~/components/ServerResponseContext'
import LocaleBar from '~/components/LocaleBar/dynamic'
import { LanguageProvider } from '~/context/Language'
import CookieToast from '~/components/CookieToast/dynamic'
import { IntercomProvider } from '~/components/IntercomProvider'
import FacebookProvider from '~/components/FacebookContext'
import LocaleBarProvider from '~/components/LocaleBarContext'
import { getLanguageCodeByLocale } from '~/utils/localization'
import Referrals from '~/components/Referrals'
import { TranslationProvider } from '~/components/TranslationContext'
import GoogleProvider from '~/components/GoogleContext'
import { setLogger as setOptimizelyLogger } from '@optimizely/react-sdk'
import getConfig from 'next/config'
import { GeoInfoProvider } from '~/components/GeoInfoContext'
import DefaultSeo from '~/components/DefaultSeo'
import { CurrentUrlProvider } from '~/components/CurrentUrlContext'
import { DeviceInfoProvider } from '~/components/DeviceInfoContext'
import FeatureFlagProvider from '~/components/FeatureFlagContext'
import { GraphQLProvider } from '~/components/GraphQLProvider'

const { publicRuntimeConfig } = getConfig()
const {
  NEXT_PUBLIC_BRAZE_SANDBOX_URL,
  NEXT_PUBLIC_BRAZE_URL,
  NEXT_PUBLIC_BRAZE_SANDBOX_KEY,
  NEXT_PUBLIC_BRAZE_KEY,
  NEXT_PUBLIC_FACEBOOK_APP_ID,
  NEXT_PUBLIC_AUTHENTICATION_GOOGLE_CLIENT_ID,
  NEXT_PUBLIC_INTERCOM_APP_ID,
} = publicRuntimeConfig

const Braze = dynamic(() => import('~/components/Braze'), { ssr: false })

const dev = process.env.NODE_ENV !== 'production'

if (!dev) {
  setOptimizelyLogger(null)
}

const MyApp = ({ Component, pageProps = {} }) => {
  const {
    loggedInUser,
    currentUrl,
    deviceInfo,
    geoInfo,
    isTrackingAllowed = false,
    enabledBetaFeatures = [],
    initialIntercomState,
  } = pageProps
  const router = useRouter()
  const locale = router.locale || 'en'
  const language = getLanguageCodeByLocale(locale)
  const [tracked, setTracked] = useState(false)

  useEffect(() => {
    const handleRouteChange = path => {
      page(path)
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    if (!tracked) {
      setTracked(true)

      // Track initial page-view
      page(router.asPath)

      // Track referrer-info once per session
      pageSource(router.asPath)
    }
  }, [router.asPath, tracked, loggedInUser])

  const useBrazeSandbox = enabledBetaFeatures.includes('BRAZE_SANDBOX')
  const brazeURL = useBrazeSandbox ? NEXT_PUBLIC_BRAZE_SANDBOX_URL : NEXT_PUBLIC_BRAZE_URL
  const brazeKey = useBrazeSandbox ? NEXT_PUBLIC_BRAZE_SANDBOX_KEY : NEXT_PUBLIC_BRAZE_KEY

  return (
    <ToastProvider>
      <TranslationProvider translations={pageProps.translations}>
        <GraphQLProvider initialState={pageProps.initialApolloState}>
          <DeviceInfoProvider deviceInfo={deviceInfo}>
            <GeoInfoProvider geoInfo={geoInfo}>
              <CurrentUrlProvider currentUrl={currentUrl}>
                <LanguageProvider>
                  <NProgress />
                  <BaseStyles />
                  <DefaultSeo />
                  <FacebookProvider appId={NEXT_PUBLIC_FACEBOOK_APP_ID}>
                    <GoogleProvider clientId={NEXT_PUBLIC_AUTHENTICATION_GOOGLE_CLIENT_ID}>
                      <LoggedInUserProvider data={{ deviceInfo }}>
                        <IntercomProvider
                          appId={NEXT_PUBLIC_INTERCOM_APP_ID}
                          initialState={initialIntercomState}
                        >
                          <LocaleBarProvider language={language}>
                            <FeatureFlagProvider loggedInUser={loggedInUser}>
                              <Referrals />
                              <ConsentDialog />
                              <LocaleBar />
                              <NetworkStatus />
                              <Component {...pageProps} />
                              <CookieToast />
                              <Braze
                                brazeURL={brazeURL}
                                brazeKey={brazeKey}
                                isTrackingAllowed={isTrackingAllowed}
                              />
                            </FeatureFlagProvider>
                          </LocaleBarProvider>
                        </IntercomProvider>
                      </LoggedInUserProvider>
                    </GoogleProvider>
                  </FacebookProvider>
                </LanguageProvider>
              </CurrentUrlProvider>
            </GeoInfoProvider>
          </DeviceInfoProvider>
        </GraphQLProvider>
      </TranslationProvider>
    </ToastProvider>
  )
}

export default withServerResponse(MyApp)
